'use client';

import type { JSX, ReactNode } from 'react';

import ProjectionLabel, { ProjectionType } from 'DesignSystem2/Projection/ProjectionLabel';
import Palette from 'DesignSystem2/Theme/Palette';
import { toVariableLengthFloat } from 'utils/caseing';

import { useGraphPayload } from '../interaction/GraphInteractionContext';
import GraphHeaderShell from './GraphHeaderShell';
import type { MarketPriceHistoryPayload } from './payloads';

interface MarketPriceHistoryHeaderProps {
    /** Optional toggle/widget rendered to the right of the projection. */
    rightSlot?: ReactNode;
}

/**
 * Build the `<ProjectionLabel>` for a market-price-history payload, or
 * `undefined` when the body suppresses the projection (`hideProjection`).
 * Extracted so the knockout left-slot (`KnockoutPriceLabel`) can reuse the
 * exact same visual without depending on the shell.
 */
export const renderMarketPriceHistoryProjection = (payload: MarketPriceHistoryPayload): JSX.Element | undefined => {
    if (payload.hideProjection) return undefined;
    return (
        <ProjectionLabel
            type={payload.useCents ? ProjectionType.PRICE : ProjectionType.CHANCE}
            projection={payload.projection}
            text={payload.noDataState ? '' : undefined}
            change={payload.projectionChange}
            changeText={toVariableLengthFloat(Math.abs(payload.projectionChange), 1)}
            color={payload.noDataState ? Palette.Text.x30 : payload.vibrant ? Palette.Yes.x10 : Palette.Text.x10}
            dontAnimate={payload.isHovering}
            size={payload.isCompact ? 'small' : 'large'}
            hideTooltip={payload.isCompact}
        />
    );
};

/**
 * Header for a single-market price-history chart (binary or knockout).
 * Builds the `<ProjectionLabel>` from primitive payload data and forwards
 * it to the shared shell along with any caller-supplied right-slot.
 */
const MarketPriceHistoryHeader = ({ rightSlot }: MarketPriceHistoryHeaderProps) => {
    const payload = useGraphPayload<MarketPriceHistoryPayload>('marketPriceHistory');
    if (!payload) return null;
    if (payload.chartUnavailable) {
        return <GraphHeaderShell rightSlot={rightSlot} />;
    }
    return <GraphHeaderShell projection={renderMarketPriceHistoryProjection(payload)} rightSlot={rightSlot} />;
};

export default MarketPriceHistoryHeader;
